import React from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import * as S from './Ads.styles';
import { Footer } from '@/components/Footer/Footer';
import { TabBar } from '@/components/TabBar/TabBar';
import { PageTopControls } from '@/components/PageTopControls';
import { SearchBaseResults } from '@/components/SearchBaseResults/SearchBaseResults';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { useBanner } from '@/hooks/useBanner';
import { VipAndLatestAds } from '@/components/AdsWithInfiniteScroll/VipAndLatestAds';
import { UserAdsWithInfiniteScroll } from '@/components/AdsWithInfiniteScroll/UserAdsWithInfiniteScroll';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import { AdsSection } from '@/components/AdsSection/AdsSection';
import { CatalogueGrid } from '@/components/CatalogueGrid/CatalogueGrid';
import { SearchPanel } from '@/components/SearchPanel';
import { FiltersButton } from '@/components/Filters/FiltersButton/FiltersButton';
import { usePageTopControlsStore } from '@/store/usePageTopControlsStore';
import { useServerDataContext } from '@/contexts';
import { InlineFiltersSkeletonLoader } from '@/components/Filters/InlineFilters/InlineFiltersSkeletonLoader';
import { useResponsive } from '@/hooks/useResponsive';
import { DesktopFooter } from '@/components/Footer';

const VipAdsWithInfiniteScroll = dynamic(
  () =>
    import('@/components/AdsWithInfiniteScroll/VipAdsWithInfiniteScroll').then(
      mod => mod.VipAdsWithInfiniteScroll
    ),
  {
    ssr: false,
    loading: () => (
      <AdsSection adsLoading chunkSize={AdsChunkSize.Middle} hasPaddingTop />
    ),
  }
);

const InlineFilters = dynamic(
  () =>
    import('@/components/Filters/InlineFilters').then(mod => mod.InlineFilters),
  {
    ssr: false,
    loading: () => <InlineFiltersSkeletonLoader />,
  }
);

export const AdsView = () => {
  const { t } = useTranslation();
  const { currentCategory: serverCurrentCategory, regions: serverRegions } =
    useServerDataContext();
  const { isDesktop } = useResponsive();
  const {
    isVipAds,
    isAllCategories,
    isFirstLevel,
    isSubcategories,
    currentCategory = serverCurrentCategory,
    isUserAds,
  } = useCurrentCategory();
  const { topBanner } = useBanner({ isDesktop });
  const hasCategories =
    (isAllCategories || isFirstLevel) && currentCategory?.id && !isVipAds;
  const topControlsPosition = usePageTopControlsStore(
    state => state.topControlsPosition
  );

  function getHeaderTitle() {
    if (isVipAds) {
      return t('pages.ads.all_vip_ads');
    }

    if (isFirstLevel || isSubcategories) {
      return currentCategory?.name;
    }

    return t('pages.ads.all_categories');
  }

  function renderAdsList() {
    switch (true) {
      case isVipAds: {
        return <VipAdsWithInfiniteScroll />;
      }
      case isUserAds: {
        return <UserAdsWithInfiniteScroll />;
      }
      default: {
        return <VipAndLatestAds />;
      }
    }
  }

  return (
    <>
      {topBanner}

      <PageTopControls>
        <S.Header
          title={getHeaderTitle()}
          content={!isDesktop && <SearchPanel />}
          rightAction={<FiltersButton />}
          hasRegionSelect={false}
        />

        {!isVipAds && !isDesktop && topControlsPosition === 'relative' && (
          <SearchBaseResults />
        )}

        {hasCategories && !isDesktop && topControlsPosition === 'relative' && (
          <CatalogueGrid />
        )}

        {!isDesktop && (
          <InlineFilters isSortVisible={topControlsPosition === 'relative'} />
        )}
      </PageTopControls>

      <S.Wrapper>
        <S.Container>
          {!isVipAds && isDesktop && <SearchBaseResults />}

          {hasCategories && isDesktop && <CatalogueGrid />}

          {renderAdsList()}
        </S.Container>
      </S.Wrapper>

      {isDesktop ? <DesktopFooter serverRegions={serverRegions} /> : <Footer />}

      {!isDesktop && <TabBar />}
    </>
  );
};
